import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { signInWithEmailAndPassword, signInWithPopup, onAuthStateChanged, signOut, getMultiFactorResolver } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, functions, provider } from "../firebase/config";
import { toast } from "react-toastify";
import { httpsCallable } from "firebase/functions";
import { apiGet, apiPost } from "../api/functionsClient";
import { setSentryUser } from "../observability/sentry";
import {
  isMfaCancelled,
  isMultiFactorAuthRequired,
  mfaCancelledError,
  MFA_GATE_CHECKING,
  MFA_GATE_OK,
  MFA_GATE_REQUIRED,
  resolveMfaGate,
  subscribeMfaRequired,
} from "../layouts/v2/utils/mfa";
import { getRecaptchaToken } from "../components/module_quiz_diagnostic/recaptchaUtils";

// reCAPTCHA del «olvidé mi contraseña»: functionsPasswordReset lo verifica en el servidor
// (acción password_reset). recaptchaUtils carga el script solo al pedir el token, así que el
// AuthProvider no lo baja en cada página.
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const PASSWORD_RESET_RECAPTCHA_ACTION = "password_reset";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

// Reto del doble factor (control 2b del plan de seguridad, FEAT-20261026-mfa-totp). El modal se
// importa al primer reto: no entra en main.js ni carga Tailwind en el sitio público. Un fallo
// de red al bajar el chunk rechaza el reto (lo maneja el catch del login), sin tumbar la app.
const loadMfaChallengeModal = () =>
  import("../layouts/v2/components/mfa/MfaChallengeModal").then((m) => m.default);

// Atajo de la puerta del doble factor: ¿el ID token ya trae el segundo factor (claim
// firebase.sign_in_second_factor)? Se lee del token en caché, recién renovado por loadUserData,
// sin red. Si no se puede leer, false: decide my-permissions, como siempre.
const readTokenHasSecondFactor = async (fbUser) => {
  try {
    const result = await fbUser.getIdTokenResult(false);
    return Boolean(result?.signInSecondFactor);
  } catch (e) {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [ user, setUser ] = useState({
    email: null,
    logged: false,
    error: null
  });
  const [ load, setLoad ] = useState(true);
  const [ authResolved, setAuthResolved ] = useState(false);
  const [ reloadUser, setReloadUser ] = useState(false);
  // Reto pendiente: {id, resolver, Modal, resolve, reject}. La ref evita resolver dos veces.
  const [ mfaChallenge, setMfaChallenge ] = useState(null);
  const mfaChallengeRef = useRef(null);
  const mfaChallengeSeq = useRef(0);
  // Puerta del doble factor para SA (FEAT-20261026-mfa-totp, sesión 5): "checking" | "ok" |
  // "required". La decide loadUserData con resolveMfaGate (mfa.js) ANTES de fijar el perfil, así
  // que el router nunca ve un SA logueado con la puerta sin decidir. Para roles que no son SA es
  // siempre "ok" y PrivateRouter no la mira.
  const [ mfaGate, setMfaGate ] = useState(MFA_GATE_CHECKING);
  // Un 403 MFA_REQUIRED que llega con el portal ya cargado (functionsClient avisa) cierra la puerta
  // sin recargar. Solo fija un estado: no hay bucle aunque lleguen varios 403 seguidos.
  useEffect(() => subscribeMfaRequired(() => setMfaGate(MFA_GATE_REQUIRED)), []);
  // Contexto de Sentry (uid, rol, idc) sigue al usuario logueado; se limpia en logout.
  useEffect(() => { setSentryUser(user); }, [user]);
  // Función para solicitar una recarga
  const triggerReloadUser = () => {
    setReloadUser(prev => !prev); // Cambiar el estado para disparar la recarga
  };
  //Carga de Ip para guardar
  const getPublicIP = async () => {
      try {
          const res = await fetch('https://api.ipify.org?format=json');
          const data = await res.json();
          return data.ip;
      } catch (error) {
          console.error("No se pudo obtener la IP pública:", error);
          return "IP desconocida";
      }
  }
  const isMissingCoord = (v) => {
    if (v == null) return true;                       // null o undefined
    if (typeof v === 'number') return Number.isNaN(v); // número NaN => falta
    if (typeof v === 'string') return v.trim() === ''; // "" o "   " => falta
    return false; // cualquier otro caso lo consideramos presente
  };
  //Carga de datos de usuario y compañía
  const loadUserData = async (uid) => {
    // Forzar refresh del token para obtener claims actualizados (idc, rol)
    let tokenHasSecondFactor = false;
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
      // Atajo de la puerta: con el segundo factor en el token, "ok" de inmediato, sin esperar
      // a my-permissions.
      tokenHasSecondFactor = await readTokenHasSecondFactor(auth.currentUser);
      if (tokenHasSecondFactor) setMfaGate(MFA_GATE_OK);
    }

    const userSnap = await getDoc(doc(db, "users", uid));
    const userData = userSnap.data() || {};

    // 👇 DISPARA EL TRIGGER SOLO SI HAY idc
    if (userData.idc) {
      try {
        await apiPost("/usersV2/self-touch", { claims_touch: true });
      } catch (e) {
        console.warn("No se pudo registrar claims_touch:", e?.code || e?.message || e);
      }
    }

    const companySnap = await getDoc(doc(db, "company", userData.idc));
    const companyData = companySnap.exists() ? companySnap.data() : {};
    // true si TIENE coordenadas; false si FALTAN
    const hasCoords =
      !isMissingCoord(userData.latitud) &&
      !isMissingCoord(userData.longitud);

    const profile = {
      id: uid,
      logged: true,
      error: null,
      ...userData,
      ...companyData,
      location_cordena: hasCoords,   // <-- booleano solicitado
    };

    // Capacidades por módulo del propio user (fuente de verdad en backend).
    // Best-effort: si falla, dejamos permissions vacío y no rompemos el login.
    let permissionsError = null;
    try {
      const { data } = await apiGet("/usersV2/my-permissions");
      profile.permissions = data?.permissions || {};
    } catch (e) {
      permissionsError = e;
      console.warn("No se pudieron cargar los permisos:", e?.code || e?.message || e);
      profile.permissions = {};
    }

    // Puerta del doble factor: la misma respuesta de my-permissions decide (200 → ok, 403
    // MFA_REQUIRED → required, cualquier otro error → ok; ver resolveMfaGate). Se fija ANTES que
    // el perfil: un SA que no puede pasar nunca llega a pintar el portal, ni un instante.
    setMfaGate(resolveMfaGate({ rol: profile.rol, tokenHasSecondFactor, permissionsError }));
    setUser(profile);
  };
  // Doble factor: muestra el modal del reto y espera. Resuelve con el UserCredential de
  // resolver.resolveSignIn; rechaza con code "mfa/cancelled" si el usuario lo cierra.
  // Sirve para el login (correo y Google) y para la reautenticación de la pantalla
  // /seguridad/doble-factor (se expone en el contexto).
  const runMfaChallenge = useCallback(async (error) => {
    const resolver = getMultiFactorResolver(auth, error);
    const Modal = await loadMfaChallengeModal();
    return new Promise((resolve, reject) => {
      if (mfaChallengeRef.current) mfaChallengeRef.current.reject(mfaCancelledError());
      mfaChallengeSeq.current += 1;
      const challenge = { id: mfaChallengeSeq.current, resolver, Modal, resolve, reject };
      mfaChallengeRef.current = challenge;
      setMfaChallenge(challenge);
    });
  }, []);

  const settleMfaChallenge = useCallback((challenge, settle) => {
    if (!challenge || mfaChallengeRef.current !== challenge) return;
    mfaChallengeRef.current = null;
    setMfaChallenge(null);
    settle(challenge);
  }, []);

  // Primer factor; si la cuenta tiene segundo factor enrolado, pasa por el reto.
  const signInWithSecondFactor = async (signIn) => {
    try {
      return await signIn();
    } catch (error) {
      if (!isMultiFactorAuthRequired(error)) throw error;
      return runMfaChallenge(error);
    }
  };

  //Login con email y password
  const login = async (values) => {
    setLoad(true);
    try {
      if (!values.email || !values.password) {
        toast.error("Email y Password son requeridos.");
      return;
      }
      await signInWithSecondFactor(() => signInWithEmailAndPassword(auth, values.email, values.password));
      toast.success("Inicio de sesión exitoso");
      // Side-effect best-effort: no debe romper el login.
      // El endpoint usa req.user.uid del token (no del cliente).
      (async () => {
        try {
          const ip = await getPublicIP();
          await apiPost("/usersV2/self-touch", { last_login: true, ip });
        } catch (e) {
          console.warn("No se pudo registrar IP/lastLogin:", e?.code || e?.message || e);
        }
      })();
    } catch (error) {
      // Reto cerrado por el usuario: se propaga para que Login.js no navegue a "/".
      if (isMfaCancelled(error)) throw error;
      console.error("Login error:", error?.code, error?.message);
      if (error?.name === "ChunkLoadError") {
        toast.error("No se pudo cargar la verificación en dos pasos. Recarga la página.");
      } else {
        toast.error("Email o clave incorrecta");
      }
    } finally {
      setLoad(false)
    }

  };
  //Login de Google
  const googleLogin = async () => {
    setLoad(true);
    try {
      const { user: u } = await signInWithSecondFactor(() => signInWithPopup(auth, provider));
      // ✅ verifica el doc por UID
      const snap = await getDoc(doc(db, "users", u.uid));
      if (!snap.exists()) {
        await signOut(auth); // evita sesión sin perfil
        throw new Error("El email no está registrado");
      }
      // Side-effect: no debe romper el login.
      // El endpoint usa req.user.uid del token (no del cliente).
      (async () => {
      try {
        const ip = await getPublicIP();
        await apiPost("/usersV2/self-touch", { last_login: true, ip });
      } catch (e) {
        console.warn("No se pudo registrar IP/lastLogin:", e?.code || e?.message || e);
      }
      })();
      toast.success("Inicio de sesión exitoso.");
    // 👇 no apagues aquí el loader: lo hará onAuthStateChanged
    } catch (error) {
      // Reto cerrado por el usuario: sin sesión y sin toast. No se propaga: Login.js no
      // apaga su loader en el catch del botón de Google y el botón quedaría bloqueado.
      if (isMfaCancelled(error)) return;
      toast.error(error?.message || "Error al iniciar sesión con Google");
      setUser({ email: null, logged: false, error: error?.message || "Google login error" });
    } finally {
      setLoad(false)
    }
  };
  //Envio de correo para restablecer contraseña
  const sendPasswordResetEmail = async (emailInput) => {
      try {
          const cleanEmail = String(emailInput ?? "").trim().toLowerCase();
          if (!cleanEmail) throw new Error("El email es obligatorio.");
          // Token nuevo en cada envío: un token ya evaluado (el de functionsRecaptcha en
          // Login.js) no vale otra vez. Si no se puede obtener (script bloqueado), se envía sin
          // token: el servidor responde lo mismo, no envía y deja el motivo en su log.
          let recaptchaToken = null;
          try {
              recaptchaToken = await getRecaptchaToken(RECAPTCHA_SITE_KEY, PASSWORD_RESET_RECAPTCHA_ACTION);
          } catch (captchaErr) {
              console.error("reCAPTCHA (password_reset) error:", captchaErr?.message);
          }
          const fn = httpsCallable(functions, "functionsPasswordReset");
          const { data } = await fn({ email: cleanEmail, lang: navigator.language || "es-419", recaptchaToken });
          if (data?.success) {
              toast.success("Si el correo existe, recibirás el enlace para restablecer la contraseña.");
              return { success: true };
          }
          toast.error(data?.message || "No se pudo enviar el correo de restablecimiento.");
          return { success: false, message: data?.message || "unknown" };
      } catch (error) {
          console.error("functionsPasswordReset error:", {
              code: error?.code,
              message: error?.message,
              details: error?.details,
          });
          // Respuesta neutra para no revelar existencia
          toast.success("Si el correo existe, recibirás el enlace para restablecer la contraseña.");
          return { success: true };
      }
  };
  //Logout de usuario
  const logout = () => {
    signOut(auth)
      .then(() => {
        setUser({ email: null, logged: false, error: null });
        // Redirige al home con recarga completa: limpia los contextos en memoria.
        // AuthProvider vive fuera del BrowserRouter, así que no puede usar useNavigate.
        // (Antes aquí se llamaba a `Navigate` de react-big-calendar, que es un objeto de
        // constantes: lanzaba TypeError, nunca redirigía y metía la librería en main.js.)
        window.location.replace("/");
      })
      .catch((error) => {
        console.error("Error al cerrar sesión:", error);
      });
  };
  //Carga data del usuario
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      try {
        if (u) {
          await loadUserData(u.uid);
        } else {
          // Sin sesión la puerta vuelve a "sin decidir": la próxima cuenta la decide al cargar.
          setMfaGate(MFA_GATE_CHECKING);
          setUser({ email: null, logged: false, error: null });
        }
      } catch (e) {
        console.error("Auth resolve error:", e);
        setUser({ email: null, logged: false, error: e?.message || "auth_error" });
      } finally {
        // Marcamos que ya resolvimos (hay sesión o no) para que el router
        // pueda decidir qué mostrar sin flashear 404.
        setAuthResolved(true);
      }
    });
    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadUser, load]);
  // ----- Data de equipos que solo carga admin y super-admin ----- //
  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      googleLogin,
      load,
      setLoad,
      authResolved,
      triggerReloadUser,
      sendPasswordResetEmail,
      runMfaChallenge,
      mfaGate,
    }}>
      {children}
      {mfaChallenge && (
        <mfaChallenge.Modal
          key={mfaChallenge.id}
          resolver={mfaChallenge.resolver}
          onResolved={(credential) => settleMfaChallenge(mfaChallenge, (c) => c.resolve(credential))}
          onCancel={() => settleMfaChallenge(mfaChallenge, (c) => c.reject(mfaCancelledError()))}
        />
      )}
    </AuthContext.Provider>
  );
};
